import type { TweenEasing } from "@slot/presentation";

/**
 * Game tuning for the CHANCE (CH) detonation (`detonate_chance_symbol`). The markers first, then the barrel's Spine
 * `win` clip, which is its explosion (the clip ends with the barrel gone):
 *   - every cell outside the blast dims, and markers fade in behind the targets one after another, clockwise around the
 *     barrel from 12 o'clock;
 *   - `barrelDelaySeconds` after the last marker is in, the barrel's clip starts, in place on the cell's skeleton;
 *   - `removalStartSeconds` into the clip the blast's cells are removed: the targets play their `out` (CH and FS carry
 *     none: they shrink and burst) and vanish on the barrel's frame, while the markers fade out;
 *   - the barrel's own cell plays no removal: it leaves when its clip ends, and the dim lifts then.
 * All timing is engine seconds (PresentationTaskRunner), so it scales with turbo and stops with the dev pause.
 */
export interface ChanceTuning {
  /**
   * Runner scope of the detonation's waits (the beat before the barrel, the removal's lead, the barrel clip's end) and
   * the marker fades; the clip itself is Spine, which no slam shortens. Nothing slams this scope: a slammed wait would
   * remove the cells before the barrel blows, or release the barrel's cell mid-explosion.
   */
  readonly scope: string;
  /** The detonation sound, played as the barrel's `win` clip starts. */
  readonly activationSoundId: string;
  /** Dim on every cell outside the blast while it plays. Negative darkens; 0 = off. No-op without a dim port
   *  (`ChanceFeatureDeps.dimPort`); this game passes the win highlight's `AlphaDimEffect`. */
  readonly dimExposure: number;
  readonly dimFadeSeconds: number;
  readonly dimFadeEasing: TweenEasing;
  /** The beat between the last marker being in and the barrel's clip starting. */
  readonly barrelDelaySeconds: number;
  /**
   * When the blast's removal starts, in seconds into the barrel's `win` clip (the runner scales the wait as the clip is
   * scaled). The barrel vanishes 0.6 s into its clip and a symbol's `out` hides the symbol 0.167 s into its own, so
   * 0.433 makes the targets vanish on the barrel's frame. Clamped to the clip; without the barrel's Spine the removal
   * starts this long after the barrel's turn.
   */
  readonly removalStartSeconds: number;
  /** The markers behind the targets (`ChanceMarkersView`). */
  readonly marker: ChanceMarkerTuning;
}

/** A target marker: a rounded, semi-transparent square behind the symbol, and its fades. */
export interface ChanceMarkerTuning {
  /** Fill colour, 0xRRGGBB. */
  readonly color: number;
  /** Fill opacity, 0..1: the symbol reads through it. */
  readonly alpha: number;
  /** The square's side as a fraction of the cell's shorter edge (below 1 leaves a gap between neighbours). */
  readonly sizeRatio: number;
  /** The corner radius as a fraction of the side. */
  readonly cornerRatio: number;
  /** Each marker's fade-in. */
  readonly fadeInSeconds: number;
  /** The gap between two markers' fade-ins, clockwise around the barrel from 12 o'clock. 0 = all at once. */
  readonly staggerSeconds: number;
  /** Fade-out as the removal starts: gone when the targets vanish. */
  readonly fadeOutSeconds: number;
}

export const CHANCE_TUNING: ChanceTuning = {
  scope: "chance_detonation",
  activationSoundId: "grid_chance_collect",
  dimExposure: -0.6,
  dimFadeSeconds: 0.12,
  dimFadeEasing: "quadOut",
  // Eight neighbours: the last marker is in at 0.54 s, so the barrel starts at 0.60 s and blows at 1.20 s.
  barrelDelaySeconds: 0.06,
  removalStartSeconds: 0.433,
  marker: {
    color: 0xff9a1f,
    alpha: 0.4,
    sizeRatio: 0.94,
    cornerRatio: 0.14,
    fadeInSeconds: 0.12,
    staggerSeconds: 0.06,
    fadeOutSeconds: 0.167,
  },
};
